import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { defaultStaleTime } from '../../../../config/queryClient'
import { QueryKeys } from '../../../../enums/queryKeys'
import { usePeriodDates } from '../../../../hooks/usePeriodDates'
import { SalesReportDateSelection } from '../../enums/salesReportDateSelection'
import { fetchSalesOverviewReport } from '../../query-api'
import { FetchSalesOverviewReportParams } from '../../types/fetchSalesReportParams'
import { getSalesReportDateRange } from '../../utils/getSalesReportDateRange'

interface UseFetchSalesOverviewReportProps {
  dateSelection: SalesReportDateSelection
  period?: string
}

export const useFetchSalesOverviewReport = ({ dateSelection, period }: UseFetchSalesOverviewReportProps) => {
  const { organization } = useUserOrganization()
  const { firstFiscalYearStart } = organization || {}
  const { isLoading: isLoadingPeriodDates, fromDate: startDate, toDate: endDate } = usePeriodDates(period)

  const { startDate: salesReportStartDate, endDate: salesReportEndDate } = useMemo(
    () => getSalesReportDateRange(startDate, endDate, firstFiscalYearStart),
    [startDate, endDate, firstFiscalYearStart],
  )

  const queryParams: FetchSalesOverviewReportParams = useMemo(() => {
    return {
      dateSelection,
      startDate: salesReportStartDate,
      endDate: salesReportEndDate,
    }
  }, [dateSelection, salesReportStartDate, salesReportEndDate])

  const queryKey = useMemo(() => [QueryKeys.DocumentReportsSalesOverview, queryParams], [queryParams])

  const { data: salesOverviewReportData, isLoading: isLoadingSalesReport } = useQuery(
    queryKey,
    () => fetchSalesOverviewReport(queryParams),
    {
      enabled: !isLoadingPeriodDates,
      staleTime: defaultStaleTime,
    },
  )

  const isLoading = isLoadingPeriodDates || isLoadingSalesReport

  const salesOverviewReport = useMemo(() => {
    const report = salesOverviewReportData?.data?.data || []

    return dateSelection === SalesReportDateSelection.Payment ? report.filter(({ paidDate }) => paidDate) : report
  }, [dateSelection, salesOverviewReportData?.data?.data])

  return { isLoading, salesOverviewReport }
}
